/**
 * Making the sign-in form work with password managers.
 *
 * The form Amplify renders is, on inspection, exactly right: a real <form method=post>,
 * `name="username"` with `autocomplete="username"`, `name="password"` with
 * `autocomplete="current-password"`, and a `type="submit"` button. Autofill still did
 * not work, and neither did RoboForm, for two reasons that have nothing to do with the
 * markup.
 *
 * ONE: NOTHING WAS EVER SAVED
 * ---------------------------
 * Signing in here is an XHR. Amplify does SRP against Cognito and then swaps the React
 * tree; the page never navigates. A browser's "save this password?" prompt is driven
 * largely by a form submission followed by a navigation, so on an app like this it
 * frequently never appears - and a password manager with nothing stored has nothing to
 * autofill. The symptom reads as "autofill is broken" when the real state is "there is
 * no saved credential to fill".
 *
 * navigator.credentials.store() is the API that exists for exactly this: it tells the
 * browser, explicitly, that a sign-in succeeded and with what. The browser still asks
 * the person before saving anything - this cannot store a credential behind their back.
 *
 * TWO: THE FIELD IDS WERE NOT VALID SELECTORS
 * -------------------------------------------
 * Amplify ids its inputs from React's useId, which produces `amplify-id-:r1:`. The
 * colons are legal in an HTML id and illegal in a CSS id selector, so
 * `document.querySelector('#amplify-id-:r1:')` throws SyntaxError - verified on the
 * deployed page. A password manager that remembers "the username field on this site"
 * by building a selector from the id therefore fails outright here, which is the more
 * likely explanation for RoboForm specifically.
 *
 * They are also UNSTABLE: useId numbers by position in the render tree, so any rule
 * keyed on `:r1:` stops matching the next time the tree changes shape.
 *
 * Amplify's formFields API has no `id` option (see FormFieldOptions in
 * @aws-amplify/ui), so the ids are rewritten here after render. Only the field's own
 * `<label for>` references them - checked on the live page rather than assumed - so
 * this is a two-attribute rename and not a reach into somebody else's component tree.
 *
 * WHAT NONE OF THIS CAN DO
 * ------------------------
 * None of it reads anybody's vault, and nothing here could be made to. A page cannot
 * ask a password manager what it holds; autofill is the manager deciding, locally, that
 * a stored login matches the page. So the most this file can achieve is to make the
 * form recognisable and make sure a credential gets stored in the first place - and if
 * nothing has ever been saved, there is nothing to offer, which is the state that reads
 * on screen as "autofill is broken".
 */

/** Stable, selector-safe ids. Plain ASCII and hyphens, so `#id` parses. */
const FIELD_IDS: Record<string, string> = {
  username: 'planning-signin-username',
  password: 'planning-signin-password',
};

/**
 * A stable name and id for the form itself.
 *
 * Amplify gives it neither, and an extension-based manager has little else to
 * recognise a form by: the browser-native Credential Management API is not available
 * to an extension, so RoboForm and its kind identify a login by the page plus the
 * shape of the form, and a form with no name is one more thing they cannot key on.
 *
 * NO `action`, DELIBERATELY. It is the other attribute managers are said to like, and
 * it is the one with teeth: if Amplify ever failed to call preventDefault - a version
 * bump, an error path, a step this app does not use today - the browser would really
 * submit, and a real submit sends the password to whatever `action` points at. A
 * marginal improvement in form recognition is not worth leaving a loaded gun in the
 * markup for an upgrade to pull.
 */
const FORM_ID = 'planning-signin';

/**
 * Give the sign-in inputs ids a password manager can match on, and keep them.
 *
 * Returns a teardown. The MutationObserver is not belt-and-braces: Amplify re-renders
 * this form on every step change - a wrong password, an MFA prompt, the
 * new-password screen - and each render writes React's own id back over ours.
 */
export function stabiliseFieldIds(root: HTMLElement): () => void {
  const apply = () => {
    const form = root.querySelector('form');
    if (form && form.id !== FORM_ID) {
      form.id = FORM_ID;
      form.setAttribute('name', FORM_ID);
    }

    for (const [name, id] of Object.entries(FIELD_IDS)) {
      const input = root.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      if (!input || input.id === id) {
        continue;
      }
      /*
        The label first, while it can still be found by the old id. Reversing these
        two lines silently unlabels the field, which a screen reader notices and a
        sighted reviewer does not.

        Found by COMPARING htmlFor rather than by a selector. The id being replaced is
        the very thing that cannot go in a selector - `amplify-id-:r1:` has colons in
        it - so a selector here needs CSS.escape, and that is a global this function
        would then throw on if it were ever absent. Taking the whole id stabiliser down
        over a lookup helper is a poor trade when a comparison needs no escaping at all.
      */
      const label = input.id
        ? [...root.querySelectorAll('label')].find((l) => l.htmlFor === input.id) ?? null
        : null;
      input.id = id;
      if (label) {
        label.htmlFor = id;
      }
    }
  };

  apply();
  const observer = new MutationObserver(apply);
  observer.observe(root, { childList: true, subtree: true });
  return () => observer.disconnect();
}

/**
 * Remember what was typed into the sign-in form, so it can be offered to the browser
 * once the sign-in actually succeeds.
 *
 * Captured on submit because that is the only moment both fields are populated: by the
 * time authStatus flips, Amplify has unmounted the form. Held in a module closure,
 * never in React state - state is inspectable in devtools and survives in a component
 * tree, and this is a password. The module scope is also what lets it outlive the
 * component's own teardown, which is the whole point - see takeCredential.
 *
 * `capture: true` so it runs before Amplify's own handler, which is free to do what it
 * likes with the event afterwards.
 */
let pending: { id: string; password: string } | null = null;

/**
 * Take what was captured, if anything. Reading CLEARS it.
 *
 * Module-scoped rather than held by the component, and that is the fix for a bug that
 * made every one of the other measures in this file pointless.
 *
 * THE BUG. Both effects in LoginGate key on `authStatus`. When sign-in succeeds React
 * runs the CLEANUP of the first effect before the body of the second, so the capture
 * was torn down - clearing what it held and nulling the ref - and the "offer this to
 * the browser" effect then ran against null. The credential was discarded a moment
 * before it was due to be offered, every single time. No prompt ever appeared, nothing
 * was ever saved, and the symptom on screen was "autofill does not work" when the real
 * state was "there has never been anything to fill".
 *
 * Living outside the component is what makes it survive that teardown. It is cleared
 * when a fresh form mounts and again the moment it is read, so a wrong password
 * followed by giving up does not leave it sitting there.
 */
export function takeCredential(): { id: string; password: string } | null {
  const value = pending;
  pending = null;
  return value;
}

export function captureCredential(root: HTMLElement): { stop: () => void } {
  // A fresh form means a fresh attempt. Anything left from a previous one - a wrong
  // password, an abandoned sign-in - is dropped here rather than lingering.
  pending = null;

  const onSubmit = (event: Event) => {
    const form = (event.target as HTMLElement | null)?.closest?.('form');
    if (!form) {
      return;
    }
    const id = form.querySelector<HTMLInputElement>('input[name="username"]')?.value ?? '';
    const password =
      form.querySelector<HTMLInputElement>('input[name="password"]')?.value ?? '';
    pending = id && password ? { id, password } : null;
  };

  root.addEventListener('submit', onSubmit, true);
  return {
    // Removes the LISTENER and nothing else. It must not clear what was captured:
    // this runs on the teardown that a successful sign-in causes, which is precisely
    // when the captured credential is still needed. See takeCredential.
    stop: () => {
      root.removeEventListener('submit', onSubmit, true);
    },
  };
}

/**
 * Offer a just-used credential to the browser's password manager.
 *
 * Silent about everything. A browser without the API, a person who declines the
 * prompt, a policy that blocks it - none of those are failures worth a message on a
 * screen somebody has just successfully signed in to. Nothing here is logged, because
 * the thing it would be logging is a password.
 */
/**
 * Where the last sign-in attempt's outcome is left, for somebody debugging this.
 *
 * NEVER THE CREDENTIAL. Whether one was captured, and what the browser said about
 * storing it - nothing else. sessionStorage because it survives the reload that
 * follows a sign-in, and dies with the tab.
 *
 * This exists because the failure is invisible from both ends. rememberCredential
 * swallows everything on purpose - a Slack-free screen somebody has just successfully
 * signed in to is no place for an error about password managers - so a browser that
 * refuses to store, as a policy-managed one will, looks exactly like a browser that
 * stored it happily. Without this there is nothing to tell them apart but guesswork,
 * and guesswork is what this whole episode has been.
 */
export const DIAGNOSTIC_KEY = 'planning-signin-diagnostic';

function note(outcome: Record<string, unknown>, keepExisting = false): void {
  const line = { at: new Date().toISOString(), ...outcome };
  try {
    /*
      THE RELOAD DESTROYS THE EVIDENCE OTHERWISE, which it did on the first real
      attempt at reading this.

      A successful sign-in reloads the page. The run that follows the reload finds
      nothing captured - correctly, there is nothing left to offer - and wrote
      "captured: false, nothing to store" straight over the record of what had just
      happened. Every reading was therefore of the reload rather than of the sign-in,
      and said the same uninformative thing whatever the browser had actually done.

      So the trivial note yields to an existing one. The interesting record is the one
      written before the reload, and it is the one that survives.
    */
    if (!(keepExisting && sessionStorage.getItem(DIAGNOSTIC_KEY))) {
      sessionStorage.setItem(DIAGNOSTIC_KEY, JSON.stringify(line));
    }
  } catch {
    // Private mode, blocked storage. The console line below is the fallback.
  }
  // Logged as well as stored, so it is visible without knowing the key exists.
  console.info('[planning] sign-in password-manager diagnostic:', line);
}

export async function rememberCredential(
  credential: { id: string; password: string } | null
): Promise<void> {
  /*
    Typed here rather than pulled from lib.dom. PasswordCredential is in neither the
    TypeScript DOM library nor Safari, so the declaration and the feature detection are
    the same line of defence: if it is absent this does nothing, and the compiler has
    no opinion about a global it cannot see.
  */
  const Ctor = (
    window as unknown as {
      PasswordCredential?: new (data: { id: string; password: string }) => Credential;
    }
  ).PasswordCredential;

  if (!credential) {
    // keepExisting: this is the post-reload run, and it must not bury what the
    // sign-in itself recorded a moment ago.
    note({ captured: false, stored: 'nothing to store' }, true);
    return;
  }
  if (!Ctor || !window.isSecureContext || !navigator.credentials) {
    note({
      captured: true,
      stored: 'unsupported',
      hasPasswordCredential: Boolean(Ctor),
      secureContext: window.isSecureContext,
      hasCredentialsApi: Boolean(navigator.credentials),
    });
    return;
  }

  try {
    await navigator.credentials.store(new Ctor(credential));
    note({ captured: true, stored: 'ok' });
  } catch (e) {
    /*
      Declined, blocked, or refused by policy. STILL SWALLOWED - the person is signed
      in, and that is the only thing this screen owes them - but no longer silent to
      somebody looking for it. A managed Edge reports NotSupportedError here, which is
      the difference between "the app is broken" and "the browser was told not to".
    */
    note({ captured: true, stored: 'refused', error: String((e as Error)?.name ?? e) });
  }
}
